import { TextButton } from "@/components/ui/pressable";
import { Eye, History } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { Thread } from "@/modules/threads/threadModel";
import { threadPlainText } from "@/modules/threads/threadModel";
import {
  buildThreadHistory,
  formatAuditDate,
  type ThreadView,
} from "@/modules/threads/threadAudit";

/** "Seen by 3" — who saw the thread, first and last time. */
export const SeenByChip = ({
  views,
  memberNames,
}: {
  views: ThreadView[];
  memberNames: Map<string, string>;
}) => {
  if (!views.length) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <TextButton tone="muted" size="xs" aria-label="Seen by">
          <Eye className="size-3.5" />
          Seen by {views.length}
        </TextButton>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <p className="border-b px-3 py-2 text-xs font-semibold text-muted-foreground">
          Seen by
        </p>
        <ul className="max-h-64 divide-y overflow-y-auto">
          {views.map((view) => (
            <li key={view.member_id} className="px-3 py-2 text-sm">
              <p className="font-medium">
                {memberNames.get(String(view.member_id)) ?? "Teammate"}
              </p>
              <p className="text-xs text-muted-foreground">
                Last seen {formatAuditDate(view.last_viewed_at)}
              </p>
              {view.first_viewed_at !== view.last_viewed_at ? (
                <p className="text-xs text-muted-foreground">
                  First seen {formatAuditDate(view.first_viewed_at)}
                  {view.view_count > 1 ? ` · ${view.view_count} visits` : ""}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
};

/** Created, edited, replies, pinned, resolved and seen — with date and time. */
export const ThreadHistoryDialog = ({
  open,
  onOpenChange,
  thread,
  views,
  memberNames,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  thread: Thread;
  views: ThreadView[];
  memberNames: Map<string, string>;
}) => {
  const entries = buildThreadHistory(thread, views, (text) =>
    threadPlainText(text).slice(0, 120),
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="size-4" />
            Thread history
          </DialogTitle>
          <DialogDescription>
            Who created, edited, replied, pinned, resolved and saw this thread.
          </DialogDescription>
        </DialogHeader>
        <ol className="max-h-[60vh] space-y-0 overflow-y-auto">
          {entries.map((entry, index) => (
            <li
              key={`${entry.at}-${index}`}
              className="grid grid-cols-[8.5rem_minmax(0,1fr)] gap-3 border-b py-2 last:border-0"
            >
              <span className="text-xs text-muted-foreground tabular-nums">
                {formatAuditDate(entry.at)}
              </span>
              <span className="min-w-0 text-sm">
                <span className="font-medium">
                  {entry.memberId != null
                    ? (memberNames.get(String(entry.memberId)) ?? "Teammate")
                    : "Someone"}
                </span>{" "}
                {entry.action}
                {entry.detail ? (
                  <span className="block truncate text-xs text-muted-foreground">
                    {entry.detail}
                  </span>
                ) : null}
              </span>
            </li>
          ))}
        </ol>
      </DialogContent>
    </Dialog>
  );
};
